// Presentational artwork-upload widgets, shared by the residual
// ArtworkUploader block AND the inline cover/guts/hardcover slots rendered
// inside SchemaForm. All state lives in useLineAssets — these components are
// pure (props in, callbacks out), so the same slot renders identically
// wherever it's mounted.
//
// Thumbnail strategy:
//   - Raster images (PNG/JPEG/TIFF) and SVG render their actual bytes via the
//     same-origin preview proxy as the <img> src.
//   - PDF renders a first-page thumbnail (pdf.js via ./docThumbnail), falling
//     back to a branded file-type badge when no thumbnail can be produced.
//   - ZIP / AI / EPS (and any thumbnail miss) render a branded file-type badge
//     stamped with the extension and color-coded the way a file browser does.
//   - Files mid-upload render from the local File (image blob URL, or a
//     freshly-rendered PDF thumbnail) so the customer sees a preview
//     immediately, before the round-trip to the provider.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ARTWORK_ACCEPT,
  COMPONENT_ROLE_HELP,
  COMPONENT_ROLE_LABEL,
  acceptAttrForRole,
  isRenderableImage,
  type AssetDto,
  type ComponentRole,
  type ComponentRoleSpec,
  type ReusableAsset,
} from 'shared';

import * as uploadsApi from '../api/uploads.js';
import {
  buildDocThumbnailUrl,
  docThumbnailKind,
  type DocThumbnailKind,
} from './docThumbnail.js';
import { Modal } from './Modal.js';
import { acceptsImages, PASTE_HINT_SUPPORTED, usePasteFiles } from './usePasteFiles.js';
import { formatNumber } from '../i18n/formatters.js';
import {
  fileExtension,
  fileTypeCategory,
  formatBytes,
  lineHasMissingBarcode,
  type PendingUpload,
} from './useLineAssets.js';

// File-picker `accept` filters come from the ONE shared, role-aware accept
// policy (shared/util/upload-file-types.ts): artwork slots get the print-format
// set graphics can receive, the manuscript slot ('guts') is PDF-only, and the
// 'combined' Hands-on Prep catch-all gets no filter (the OS shows all files).
// This is presentation only — uploadFileTypeRejection is the authority.
const acceptForRole = acceptAttrForRole;

// One slot's "I don't have my final artwork yet" mark, as the line hands it to
// whichever surface renders that slot. `onChange` is absent on a locked quote:
// the mark still waives the slot's badge, but the checkbox is not offered.
export type ArtworkPendingToggle = {
  pending: boolean;
  onChange?: (next: boolean) => void;
};

// The line's per-role lookup: null for a slot the line cannot mark.
export type ArtworkPendingToggleFor = (role: ComponentRole) => ArtworkPendingToggle | null;

// One labelled upload slot scoped to a single component_role: a heading +
// help, a dropzone, and a thumbnail grid of that role's files. Used both for
// the family role-grouped slots (cover/guts historically) and the dedicated
// reveal-on-selection slots (emboss/foil/variable-data, hardcover endsheet/
// embossing). Drop the dropzone (`hideDropzone`) for read-only buckets like
// the "_other" orphan list.
export function UploadSlot({
  spec,
  label,
  help,
  assets,
  pending,
  disabled,
  onPickFiles,
  onDelete,
  onDismissPending,
  hideDropzone = false,
  domId,
  error,
  barcodeRequired = false,
  artworkPending,
  onArtworkPendingChange,
  reusable,
  onReuse,
}: {
  spec: ComponentRoleSpec;
  label?: string;
  help?: string;
  assets: AssetDto[];
  pending: PendingUpload[];
  disabled: boolean;
  onPickFiles: (files: ArrayLike<File> | null) => void;
  onDelete: (a: AssetDto) => void;
  onDismissPending: (local_id: string) => void;
  hideDropzone?: boolean;
  // Quote-level fulfillment signal → the barcode advisory below reads "required"
  // instead of "optional". See ArtworkBarcodeNotice.
  barcodeRequired?: boolean;
  // "Quote now, art to follow" for THIS slot only — one mark per artwork slot,
  // stored on the line (config.artwork_pending_roles), so ticking the cover says
  // nothing about the manuscript. When a handler is supplied the slot renders the
  // checkbox right under its dropzone, where the customer realizes they don't
  // have final art. While checked it waives this slot's "needed before you can
  // order" badge, matching the pricing pre-flight. The caller passes a handler
  // only for a slot the line can mark (linePendableArtworkRoles); omit it to hide
  // the checkbox. Hidden too once a file is in the slot — the line clears the
  // mark when the file lands.
  artworkPending?: boolean;
  onArtworkPendingChange?: (next: boolean) => void;
  // Scroll-target id for the host's missing-field summary (the pricing
  // pre-flight reveals a missing required upload by scrolling to it), namespaced
  // by line the same way FieldRow ids are. Only set for the inline cover/guts
  // slots — the residual ArtworkUploader leaves it undefined.
  domId?: string;
  // Backend/pre-flight "you need this file to get a price" message, keyed by the
  // file_reference field. Surfaced inline (red) right at the slot, mirroring the
  // FieldRow inline error so the customer reads the same message in two places
  // (near-button summary + here). Distinct from the `showMissing` badge below.
  error?: string;
  // The caller's whole Company reusable-artwork library (useLineAssets.reusable).
  // Passed UNFILTERED — this slot knows its own role, so the role filter lives
  // here once rather than at each of the four call sites. Omit (or pass an empty
  // list) to leave the reuse affordance out entirely.
  reusable?: readonly ReusableAsset[];
  onReuse?: (asset: ReusableAsset, role: ComponentRole) => Promise<void>;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const heading = label ?? COMPONENT_ROLE_LABEL[spec.role];
  const helpText = help ?? COMPONENT_ROLE_HELP[spec.role];
  const isEmpty = assets.length === 0 && pending.length === 0;
  // "Quote now, art to follow" waives the artwork-upload requirement, so a slot
  // whose file is still missing must NOT nag once the customer has ticked it.
  const showMissing = spec.required && isEmpty && !artworkPending;
  // Which slots carry the checkbox is the caller's call (the line's pendable
  // roles: the family-required components, or the Hands-on Prep catch-all on a
  // family that requires nothing — never a purely optional slot, FB-0076).
  const showPendingToggle =
    Boolean(onArtworkPendingChange) && !hideDropzone && assets.length === 0;
  return (
    <section
      className="artwork-slot"
      {...(domId ? { id: domId, tabIndex: -1 } : {})}
      aria-invalid={error ? true : undefined}
    >
      <header className="artwork-slot__header">
        <h4 className="artwork-slot__title">
          {heading}
          {spec.required && (
            <span
              className="artwork-slot__required"
              aria-label={t('uploadSlot.header.requiredAria')}
            >
              {' '}
              *
            </span>
          )}
        </h4>
        {!spec.required && !hideDropzone && (
          <span className="artwork-slot__optional">{t('uploadSlot.header.optionalTag')}</span>
        )}
        {helpText && <p className="help artwork-slot__help">{helpText}</p>}
        {showMissing && (
          <span className="badge badge--warn">{t('uploadSlot.header.neededBadge')}</span>
        )}
        {error && (
          // role=alert so the message is announced when the pre-flight injects it.
          <span className="field-error" role="alert">
            {error}
          </span>
        )}
      </header>
      {!hideDropzone && (
        // The Hands-on Prep slot ('combined') and the flat-work artwork slot
        // ('general') accept multiple files — customers drop a stack (front +
        // back, several flats) and we sort it out. All other component slots are
        // 1:1 with a single file. `compact` is dropped only for 'combined' so its
        // dropzone shows the explicit "pick one or more files" helper; 'general'
        // stays compact to match the inline cover/guts slots it sits beside.
        <Dropzone
          disabled={disabled}
          onPickFiles={onPickFiles}
          accept={acceptForRole(spec.role)}
          multiple={spec.role === 'combined' || spec.role === 'general'}
          compact={spec.role !== 'combined'}
        />
      )}
      {/* "Reuse a file you've sent before" — a repeat order shouldn't have to
          re-upload artwork Argo already holds. Deliberately QUIET, not a second
          dropzone: one link that opens a picker modal, offered only while
          the slot is still EMPTY (the moment of need) so it doesn't clutter a
          slot that's already done. Hidden entirely when the library has nothing
          for this role — a first-time customer never sees it. */}
      {!hideDropzone && !disabled && isEmpty && onReuse && (
        <ReusePicker
          role={spec.role}
          library={reusable ?? []}
          onReuse={onReuse}
        />
      )}
      {showPendingToggle && (
        // This slot's "I don't have final art yet" mark. A quiet inline
        // checkbox, not a callout box.
        <label className="artwork-slot__pending-toggle">
          <input
            type="checkbox"
            checked={artworkPending ?? false}
            onChange={(e) => onArtworkPendingChange?.(e.target.checked)}
            disabled={disabled}
          />
          <span className="help">{t('uploadSlot.pendingToggle.label')}</span>
        </label>
      )}
      {(pending.length > 0 || assets.length > 0) && (
        <AssetGrid
          assets={assets}
          pending={pending}
          disabled={disabled}
          onDelete={onDelete}
          onDismissPending={onDismissPending}
        />
      )}
      {/* "We didn't spot a barcode" advisory, scoped to THIS slot's files, so it
          lands directly under the artwork it scanned (e.g. right below the cover
          upload). Renders nothing unless this slot's role is a barcode-bearing
          face AND a scanned file came back without one (see lineHasMissingBarcode).
          Suppressed for the read-only "_other" bucket (hideDropzone) — those are
          stranded files, not a slot the customer is actively filling. */}
      {!hideDropzone && <ArtworkBarcodeNotice assets={assets} barcodeRequired={barcodeRequired} />}
    </section>
  );
}

// How many library entries a slot lists before truncating. The library is
// recency-sorted server-side, so these are the customer's most recent files —
// far more useful than an alphabetical wall. When more exist we SAY so rather
// than truncating silently.
const REUSE_LIST_MAX = 8;

// The reuse link for one slot and the picker modal it opens. Renders nothing
// unless the Company library holds something this slot can take: a file used
// for the SAME component_role before, or a role-less company file (which fits
// any slot). Keeping the filter tight is what stops a cover slot from offering
// a manuscript.
function ReusePicker({
  role,
  library,
  onReuse,
}: {
  role: ComponentRole;
  library: readonly ReusableAsset[];
  onReuse: (asset: ReusableAsset, role: ComponentRole) => Promise<void>;
}): JSX.Element | null {
  const { t } = useTranslation(['quote', 'common']);
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const matches = library.filter(
    (a) => a.component_role === role || a.component_role === null,
  );
  if (matches.length === 0) return null;
  const shown = matches.slice(0, REUSE_LIST_MAX);

  async function pick(asset: ReusableAsset): Promise<void> {
    if (busyKey !== null) return;
    setBusyKey(asset.object_key);
    try {
      await onReuse(asset, role);
      setOpen(false);
    } finally {
      setBusyKey(null);
    }
  }

  function close(): void {
    if (busyKey === null) setOpen(false);
  }

  return (
    <div className="artwork-slot__reuse">
      <button
        type="button"
        className="link artwork-slot__reuse-toggle"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {t('uploadSlot.reuse.toggle')}
      </button>
      {open && (
        <Modal
          title={t('uploadSlot.reuse.title')}
          onClose={close}
          footer={
            <button type="button" onClick={close} aria-disabled={busyKey !== null}>
              {t('common:actions.cancel')}
            </button>
          }
        >
          <ul className="artwork-slot__reuse-list">
            {shown.map((a) => (
              <li key={a.object_key}>
                <button
                  type="button"
                  className="artwork-slot__reuse-item"
                  // aria-disabled, not disabled: disabling the focused button
                  // drops focus to <body>, out of the dialog's Tab trap.
                  aria-disabled={busyKey !== null}
                  onClick={() => void pick(a)}
                >
                  <span className="artwork-slot__reuse-name">{a.filename}</span>
                  <span className="help artwork-slot__reuse-meta">
                    {busyKey === a.object_key
                      ? t('uploadSlot.reuse.attaching')
                      : [a.source_reference, a.size_bytes != null ? formatBytes(a.size_bytes) : null]
                          .filter(Boolean)
                          .join(' · ')}
                  </span>
                </button>
              </li>
            ))}
            {matches.length > shown.length && (
              <li className="help artwork-slot__reuse-more">
                {t('uploadSlot.reuse.truncated', { count: shown.length })}
              </li>
            )}
          </ul>
        </Modal>
      )}
    </div>
  );
}

// The thumbnail grid of pending + uploaded files. Shared by UploadSlot and
// the flat layout (ArtworkUploader). `loadingPlaceholder` shows the "loading"
// note while the initial asset list is in flight (flat layout only).
export function AssetGrid({
  assets,
  pending,
  disabled,
  onDelete,
  onDismissPending,
  loadingPlaceholder = false,
}: {
  assets: AssetDto[];
  pending: PendingUpload[];
  disabled: boolean;
  onDelete: (a: AssetDto) => void;
  onDismissPending: (local_id: string) => void;
  loadingPlaceholder?: boolean;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  return (
    <div className="artwork-uploader__grid">
      {pending.map((p) => (
        <PendingTile key={p.local_id} pending={p} onDismiss={() => onDismissPending(p.local_id)} />
      ))}
      {assets.map((a) => (
        <AssetTile key={a.id} asset={a} onDelete={() => onDelete(a)} disabled={disabled} />
      ))}
      {loadingPlaceholder && <p className="help">{t('uploadSlot.grid.loading')}</p>}
    </div>
  );
}

// Non-blocking advisory shown when a slot's uploaded artwork was scanned and no
// barcode was detected on any of its files (see useLineAssets.lineHasMissingBarcode
// + detectBarcode.ts). Rendered per upload slot so it sits directly under the
// artwork it scanned — most notably right below the cover upload, where a
// book/booklet barcode (ISBN/UPC) belongs. Purely informational — it NEVER blocks
// placing the order (V1); it just flags a likely-missing barcode, so a customer
// isn't surprised later. Renders nothing when a barcode was found, when this
// slot's role isn't a barcode-bearing face, when nothing could be scanned, or
// before any scan has resolved.
//
// The copy is CONTEXT-AWARE (`barcodeRequired`): a barcode is genuinely required
// only when Argo will warehouse + fulfill the order from inventory (every pick is
// scanned) or it's sold in a store — see shared `orderRequiresBarcode`. For a
// piece that's just printed and handed over (a business card, a marketing insert)
// it's optional. So the same missing-barcode signal reads as "Required for
// fulfillment" or "Optional", adjusting live as the customer toggles the
// quote-level Fulfillment service.
//
// Surface: banner--info (Argo blue) in BOTH cases, NOT banner--warn (amber).
// Amber is this app's "can't place the order until you fix it" signal ("Needed
// before you can place the order"); even the required-for-fulfillment variant is
// advisory in V1 (it never blocks submit), so the word "Required" in the copy
// carries the urgency without hijacking the amber=hard-block convention.
//
// role=status + aria-live=polite + aria-atomic: the result lands asynchronously
// (after the upload + scan finish), so a screen reader should hear the whole
// notice when it appears — matching the QuoteApprovalPanel / page-count pattern.
export function ArtworkBarcodeNotice({
  assets,
  barcodeRequired = false,
}: {
  assets: readonly AssetDto[];
  // See the block comment above — true ⇒ "required for fulfillment" copy.
  barcodeRequired?: boolean;
}): JSX.Element | null {
  const { t } = useTranslation(['quote', 'common']);
  if (!lineHasMissingBarcode(assets)) return null;
  return (
    <div
      className="banner banner--info artwork-barcode-notice"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {barcodeRequired ? (
        <>
          <strong>{t('uploadSlot.barcodeNotice.requiredTitle')}</strong>
          <p className="artwork-barcode-notice__body">
            {t('uploadSlot.barcodeNotice.requiredBody')}
          </p>
        </>
      ) : (
        <>
          <strong>{t('uploadSlot.barcodeNotice.optionalTitle')}</strong>
          <p className="artwork-barcode-notice__body">
            {t('uploadSlot.barcodeNotice.optionalBody')}
          </p>
        </>
      )}
    </div>
  );
}

// The upload arrow every dropzone leads with, shared with the content uploader.
export function UploadIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M24 30V12M24 12l-7 7M24 12l7 7M10 30v4a4 4 0 004 4h20a4 4 0 004-4v-4"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Shared dropzone ──────────────────────────────────────────────────
export function Dropzone({
  disabled,
  onPickFiles,
  accept = ARTWORK_ACCEPT,
  multiple = false,
  compact = false,
}: {
  disabled: boolean;
  onPickFiles: (files: ArrayLike<File> | null) => void;
  accept?: string;
  multiple?: boolean;
  compact?: boolean;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pasteProps = usePasteFiles(onPickFiles, !disabled);
  // Paste works on every dropzone, but the prompt offers it only where an image
  // is an accepted answer: on the PDF-only manuscript slot it would invite a
  // paste the gate refuses.
  const offersPaste = PASTE_HINT_SUPPORTED && acceptsImages(accept);
  return (
    <button
      type="button"
      {...pasteProps}
      className={
        'artwork-uploader__dropzone' +
        (isDragging ? ' artwork-uploader__dropzone--active' : '') +
        (compact ? ' artwork-uploader__dropzone--compact' : '')
      }
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        onPickFiles(e.dataTransfer.files);
      }}
      disabled={disabled}
    >
      <span className="artwork-uploader__dropzone-icon" aria-hidden="true">
        <UploadIcon />
      </span>
      <span className="artwork-uploader__dropzone-text">
        <strong>
          {offersPaste
            ? t(
                multiple
                  ? 'uploadSlot.dropzone.promptMultiplePaste'
                  : 'uploadSlot.dropzone.promptSinglePaste',
              )
            : t(multiple ? 'uploadSlot.dropzone.promptMultiple' : 'uploadSlot.dropzone.promptSingle')}
        </strong>
        {!compact && (
          <span className="help">
            {t(multiple ? 'uploadSlot.dropzone.helpMultiple' : 'uploadSlot.dropzone.helpSingle')}
          </span>
        )}
      </span>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={(e) => {
          onPickFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </button>
  );
}

// ── tiles ────────────────────────────────────────────────────────────

// Render (and own the lifetime of) a document thumbnail for one tile.
//   - kind null      → no-op (image / unsupported type; the caller handles it)
//   - loadBytes      → resolves the source Blob: the local File for a pending
//                      upload, or the proxied bytes for an uploaded asset
// Keyed by `cacheKey` so it re-runs per file and revokes the previous object
// URL on change/unmount. Never throws; `failed` just means "show the badge".
function useDocThumbnail(
  cacheKey: string,
  kind: DocThumbnailKind | null,
  loadBytes: () => Promise<Blob | null>,
): { url: string | null; failed: boolean } {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    // Reset first so a kind/file change (or a flip to a non-doc kind) clears
    // any prior failed/url state before we decide whether to render again.
    setUrl(null);
    setFailed(false);
    if (!kind) return;
    let cancelled = false;
    let created: string | null = null;
    void (async () => {
      try {
        const blob = await loadBytes();
        const built = blob ? await buildDocThumbnailUrl(blob) : null;
        if (cancelled) {
          if (built) URL.revokeObjectURL(built);
          return;
        }
        if (built) {
          created = built;
          setUrl(built);
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
    // loadBytes is identified by cacheKey — callers pass a fresh closure each
    // render, so depending on its identity would thrash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, kind]);
  return { url, failed };
}

function PendingTile({
  pending,
  onDismiss,
}: {
  pending: PendingUpload;
  onDismiss: () => void;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const pct = Math.round(pending.progress * 100);
  // Images preview from the local blob URL; a PDF renders a first-page
  // thumbnail from the same local File — no provider round-trip needed yet.
  const docKind = pending.preview_url
    ? null
    : docThumbnailKind(pending.file.type, pending.file.name);
  const doc = useDocThumbnail(pending.local_id, docKind, async () => pending.file);
  const previewSrc = pending.preview_url ?? doc.url;
  const isDocThumb = !pending.preview_url && Boolean(doc.url);
  return (
    <div className="artwork-tile artwork-tile--pending">
      <div className="artwork-tile__preview">
        {previewSrc ? (
          <img src={previewSrc} alt={pending.file.name} />
        ) : (
          <FileTypeBadge filename={pending.file.name} />
        )}
        {isDocThumb && <DocTypeChip filename={pending.file.name} />}
        {!pending.error && (
          <div className="artwork-tile__progress">
            <div className="artwork-tile__progress-bar" style={{ width: `${pct}%` }} />
            <span className="artwork-tile__progress-label">
              {pct < 100
                ? t('uploadSlot.tile.uploadingProgress', { percent: formatNumber(pct) })
                : t('uploadSlot.tile.finalizing')}
            </span>
          </div>
        )}
      </div>
      <div className="artwork-tile__meta">
        <span className="artwork-tile__name" title={pending.file.name}>
          {pending.file.name}
        </span>
        <span className="artwork-tile__size">{formatBytes(pending.file.size)}</span>
      </div>
      {pending.error && (
        // role=alert so a client-side rejection (e.g. a Word file dropped on
        // the manuscript slot) is announced the moment it appears — it's
        // injected asynchronously, which a screen reader would otherwise miss.
        <div className="artwork-tile__error" role="alert">
          <span>{pending.error}</span>
          <button
            type="button"
            className="link"
            onClick={onDismiss}
            aria-label={t('uploadSlot.tile.dismissAria', { name: pending.file.name })}
          >
            {t('uploadSlot.tile.dismiss')}
          </button>
        </div>
      )}
    </div>
  );
}

function AssetTile({
  asset,
  onDelete,
  disabled,
}: {
  asset: AssetDto;
  onDelete: () => void;
  disabled: boolean;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const isImage = isRenderableImage(asset.content_type);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewErrored, setPreviewErrored] = useState(false);

  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    void uploadsApi
      .fetchAssetPreviewBlobUrl(asset.id)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setPreviewBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewErrored(true);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [asset.id, isImage]);

  // PDF / .docx: pull the (capped) source bytes through the same-origin proxy
  // and render a first-page thumbnail client-side. No-op for images (kind null).
  const docKind = isImage
    ? null
    : docThumbnailKind(asset.content_type, asset.original_filename);
  const doc = useDocThumbnail(asset.id, docKind, () =>
    uploadsApi.fetchAssetPreviewBlob(asset.id),
  );

  const showImage = isImage && previewBlobUrl && !previewErrored;
  const previewSrc = showImage ? previewBlobUrl : doc.url;
  const isDocThumb = !isImage && Boolean(doc.url);
  return (
    <div className="artwork-tile">
      <div className="artwork-tile__preview">
        {previewSrc ? (
          <img src={previewSrc} alt={asset.original_filename} />
        ) : (
          <FileTypeBadge filename={asset.original_filename} />
        )}
        {isDocThumb && <DocTypeChip filename={asset.original_filename} />}
        <span className="artwork-tile__check" aria-label={t('uploadSlot.tile.uploadedAria')}>
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
            <path
              d="M3 8.5l3 3 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </span>
      </div>
      <div className="artwork-tile__meta">
        <span className="artwork-tile__name" title={asset.original_filename}>
          {asset.original_filename}
        </span>
        <span className="artwork-tile__size">{formatBytes(asset.size_bytes)}</span>
      </div>
      <div className="artwork-tile__actions">
        <a href={asset.download_url} target="_blank" rel="noopener noreferrer" className="link">
          {t('uploadSlot.tile.open')}
        </a>
        {!disabled && (
          <button type="button" className="link link--danger" onClick={onDelete}>
            {t('uploadSlot.tile.remove')}
          </button>
        )}
      </div>
    </div>
  );
}

// Small corner pill stamped on a rendered document thumbnail, so a PDF/docx
// preview still reads as "this is a PDF" at a glance — the branding cue the
// rasterized page itself doesn't carry.
function DocTypeChip({ filename }: { filename: string }): JSX.Element {
  return (
    // aria-hidden: the adjacent <img alt={filename}> already conveys the file
    // (extension included); the chip is a visual affordance, not new content.
    <span
      className="artwork-tile__type-chip"
      data-filetype={fileTypeCategory(filename)}
      aria-hidden
    >
      {fileExtension(filename)}
    </span>
  );
}

// Fallback tile for files with no thumbnail (zip / .ai / .eps, an unrenderable
// .doc, a thumbnail miss). A page glyph + the extension on a color-coded tag.
function FileTypeBadge({ filename }: { filename: string }): JSX.Element {
  const ext = fileExtension(filename);
  return (
    <div className="artwork-tile__filetype" data-filetype={fileTypeCategory(filename)}>
      <svg viewBox="0 0 48 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path
          d="M4 4a4 4 0 014-4h22l14 14v38a4 4 0 01-4 4H8a4 4 0 01-4-4V4z"
          fill="currentColor"
          opacity="0.12"
        />
        <path d="M30 0v10a4 4 0 004 4h10" stroke="currentColor" strokeWidth="2" fill="none" />
      </svg>
      <span className="artwork-tile__filetype-label">{ext}</span>
    </div>
  );
}
